import React from 'react';
import {
  Paper,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Chip,
  Stack,
  Divider
} from '@mui/material';
import { useDroppable, useDraggable } from '@dnd-kit/core';
import { useTranslation } from 'react-i18next';
import DishEditDialog from '../dishes/DishEditDialog';

// Draggable dish chip for event plans
const DraggableEventDishChip = ({ dish, index, onDishClick }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useDraggable({
    id: `event-dish-${index}`,
    data: {
      type: 'eventDish',
      dish: dish,
      dishIndex: index
    }
  });

  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Chip
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      label={dish.name}
      size="small"
      variant="filled"
      color={dish.type === 'restaurant' ? "warning" : "primary"}
      title={dish.description}
      onClick={(e) => {
        e.stopPropagation();
        onDishClick(dish);
      }}
      sx={{ 
        mb: 0.5, 
        mr: 0.5, 
        cursor: 'grab',
        '&:active': {
          cursor: 'grabbing'
        }
      }}
    />
  );
};

const EventPlanList = ({ plan }) => {
  const { t } = useTranslation();
  const [selectedDish, setSelectedDish] = React.useState(null);
  const [dialogOpen, setDialogOpen] = React.useState(false);

  const handleDishClick = (dish) => {
    setSelectedDish(dish);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setSelectedDish(null);
    setDialogOpen(false);
  };

  const {
    isOver,
    setNodeRef
  } = useDroppable({
    id: 'event-plan',
    data: {
      type: 'event',
      accepts: ['dish', 'restaurant']
    }
  });

  if (!plan) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          {t('plans.noEventPlan')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t('plans.createEventToGetStarted')}
        </Typography>
      </Paper>
    );
  }

  const { event_name, event_date, guests, description, items = [] } = plan;

  const paperStyle = {
    p: 3,
    backgroundColor: isOver ? '#e3f2fd' : 'white',
    border: isOver ? '2px dashed #2196f3' : '1px solid #e0e0e0',
    transition: 'all 0.2s ease-in-out'
  };

  return (
    <Paper ref={setNodeRef} sx={paperStyle}>
      {/* Event Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          {event_name || t('plans.untitledEvent')}
        </Typography>
        
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <Chip 
            label={`${t('plans.date')}: ${event_date}`} 
            variant="outlined" 
            color="primary" 
          />
          <Chip 
            label={`${guests} ${t('plans.guests')}`} 
            variant="outlined" 
            color="secondary" 
          />
        </Stack>

        {description && (
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        )}
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Event Items */}
      <Typography variant="h6" gutterBottom>
        {t('plans.menuItems')}
      </Typography>

      {items.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
          {t('plans.noItemsYet')}
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
          {items.map((item, index) => (
            <DraggableEventDishChip
              key={index}
              dish={item}
              index={index}
              onDishClick={handleDishClick}
            />
          ))}
        </Box>
      )}

      {/* Drop zone indicator */}
      <Box 
        sx={{ 
          border: isOver ? '2px solid #2196f3' : '2px dashed #e0e0e0', 
          borderRadius: 1, 
          p: 3, 
          textAlign: 'center',
          mt: 2,
          backgroundColor: isOver ? '#e3f2fd' : '#fafafa',
          transition: 'all 0.2s ease-in-out'
        }}
      >
        <Typography 
          variant="body2" 
          color={isOver ? 'primary' : 'text.secondary'}
          sx={{ fontWeight: isOver ? 'bold' : 'normal' }}
        >
          {isOver ? t('plans.dropDishHere') : t('plans.dragDishesHere')}
        </Typography>
      </Box>

      {/* Dish Edit Dialog */}
      <DishEditDialog
        open={dialogOpen}
        dish={selectedDish}
        onClose={handleDialogClose}
      />
    </Paper>
  );
};

export default EventPlanList;